import type { ContentType, SortByOption } from '@kimuno/api-sdk';
import * as yup from 'yup';

import { pageContentTypes, searchSettings } from '../../constants';
import type { PageContentType, SearchFilters } from '../../types/search';

// Note, yup has an `ensure` function that uses this behavior, however
// it also has the non optional side effect of converting undefined to
// an empty array, which then creates problems
const stringArray = <T extends string = string>(values?: T[]) =>
	yup
		.array()
		.of(values ? yup.string().oneOf(values) : yup.string())
		.transform((value, originalValue) => {
			if (originalValue && !Array.isArray(originalValue)) {
				return [originalValue];
			}
			return value;
		}) as yup.ArraySchema<T>;

const stringValue = <T extends string = string>(values?: T[], defaultValue?: T) => {
	let stringSchema = values ? yup.string().oneOf(values) : yup.string();
	if (defaultValue) {
		stringSchema = stringSchema.default(defaultValue);
	}
	return yup.lazy((value) =>
		value === null ? yup.mixed().strip(true) : stringSchema
	) as yup.Schema<T>;
};

const searchQuerySchema = yup
	.object<SearchFilters>({
		contentType: stringArray<ContentType>(['course', 'document', 'video']),
		pageContentType: stringArray<PageContentType>(pageContentTypes.map((t) => t.key)),
		learningFormat: stringArray(),
		classYear: stringArray(),
		maxClassYear: yup.number(),
		minClassYear: yup.number(),
		publisher: stringArray(),
		schoolType: stringArray(),
		subject: stringArray(),
		subTopic: stringArray(),
		sortBy: stringValue<SortByOption>(['relevance', 'createdDate'], searchSettings.defaultSortBy),
		teachingFormat: stringArray(),
		teachingMethod: stringArray(),
		// Coerce only the genuine boolean-ish values; anything else (e.g. a malformed
		// double-encoded query blob, SEAR-518) degrades to undefined instead of throwing.
		// yup.boolean()'s built-in coercion asserts the type and would throw on a bad
		// string, so we transform off yup.mixed() and emit boolean | undefined ourselves.
		inclusionAdapted: yup.mixed().transform((_value, originalValue) => {
			if (originalValue === true || originalValue === 'true') return true;
			if (originalValue === false || originalValue === 'false') return false;
			return undefined;
		}) as yup.Schema<boolean | undefined>,
		term: stringValue(),
		workload: stringArray(),
		courseTheme: stringArray(),
		courseType: stringArray(),
		courseDuration: stringArray(),
	})
	.noUnknown(true);

export default searchQuerySchema;
